import { useEffect, useMemo, useState } from "react";
import { Card, Spinner, Alert, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";

const normalizeRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const MODULES = [
  { title: "Production Plans", path: "/employee/plant/production-plans", endpoint: "/employee/plant/production-plans", perm: "production:plan" },
  { title: "Production Reports", path: "/employee/plant/production-reports", endpoint: "/employee/plant/production-reports", perm: "production:report" },
  { title: "Maintenance Logs", path: "/employee/plant/maintenance-logs", endpoint: "/employee/plant/maintenance-logs", perm: "maintenance:log" },
  { title: "Wastage Logs", path: "/employee/plant/wastage-logs", endpoint: "/employee/plant/wastage-logs", perm: "wastage:view" },
  { title: "Stock Consumption", path: "/employee/plant/stock-consumption", endpoint: "/employee/plant/stock-consumption", perm: "stock:consumption:view" },
  { title: "Shift Plans", path: "/employee/plant/shift-plans", endpoint: "/employee/plant/shift-plans", perm: "plant:shift:manage" },
  { title: "Overtime Approvals", path: "/employee/plant/overtime-approvals", endpoint: "/employee/plant/overtime-approvals", perm: "plant:overtime:approve" },
  { title: "Utilization", path: "/employee/plant/utilization", endpoint: "/employee/plant/utilization", perm: "plant:utilization:view" },
  { title: "Breakdowns", path: "/employee/plant/breakdowns", endpoint: "/employee/plant/breakdowns", perm: "plant:breakdown:view" },
  { title: "Maintenance Schedules", path: "/employee/plant/maintenance-schedules", endpoint: "/employee/plant/maintenance-schedules", perm: "plant:maintenance:approve" },
  { title: "Supplier Quality Workflow", path: "/employee/plant/supplier-quality-workflow", endpoint: "/employee/plant/supplier-quality-dispatches", perm: "plant:quality:review" },
  { title: "Quality Reviews", path: "/employee/plant/quality-reviews", endpoint: "/employee/plant/quality-reviews", perm: "plant:quality:review" },
  { title: "Wastage Analysis", path: "/employee/plant/wastage-analysis", endpoint: "/employee/plant/wastage-analysis", perm: "wastage:view" },
  { title: "Material Movement", path: "/employee/plant/material-movement", endpoint: "/employee/plant/material-movement", perm: "plant:material-movement:approve" },
  { title: "Contractor Work Approval", path: "/employee/plant/contractor-work-entries", endpoint: "/employee/plant/contractor-work-entries", perm: "plant:contractor-work:approve" },
  { title: "Inventory Alerts", path: "/employee/plant/inventory-alerts", endpoint: "/employee/plant/inventory-alerts", perm: "plant:inventory:alert" },
  { title: "Safety Incidents", path: "/employee/plant/safety-incidents", endpoint: "/employee/plant/safety-incidents", perm: "plant:safety:manage" },
  { title: "Compliance Audits", path: "/employee/plant/compliance-audits", endpoint: "/employee/plant/compliance-audits", perm: "plant:compliance:manage" },
  { title: "Coordination Logs", path: "/employee/plant/coordination-logs", endpoint: "/employee/plant/coordination-logs", perm: "plant:coordination:manage" },
  { title: "Performance Reports", path: "/employee/plant/performance-reports", endpoint: "/employee/plant/performance-reports", perm: "plant:reports:manage" },
];

export default function PlantDepartmentDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [perms, setPerms] = useState([]);
  const [counts, setCounts] = useState({});
  const [attendanceStatus, setAttendanceStatus] = useState("Not Marked");
  const [idStatus, setIdStatus] = useState("Not Generated");

  const visibleModules = useMemo(() => {
    const set = new Set(perms || []);
    return MODULES.filter((m) => set.has(m.perm));
  }, [perms]);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const meRes = await API.get("/auth/me");
      const nextPerms = meRes.data?.employeeRole?.permissions || [];
      setPerms(Array.isArray(nextPerms) ? nextPerms : []);

      const attendanceReq = API.get("/attendance/today").catch(() => ({ data: null }));
      const idReq = API.get("/employee/my-idcard").catch(() => ({ data: null }));
      const moduleReqs = MODULES.map((m) =>
        API.get(m.endpoint)
          .then((res) => ({
            key: m.path,
            count: m.path === "/employee/plant/contractor-work-entries"
              ? Array.isArray(res.data?.entries)
                ? res.data.entries.length
                : 0
              : normalizeRows(res.data).length,
          }))
          .catch(() => ({ key: m.path, count: 0 }))
      );

      const [attendanceRes, idRes, ...moduleRes] = await Promise.all([
        attendanceReq,
        idReq,
        ...moduleReqs,
      ]);

      setAttendanceStatus(attendanceRes?.data?.status || "Not Marked");
      setIdStatus(idRes?.data?.idCardStatus || "Not Generated");
      setCounts(
        moduleRes.reduce((acc, row) => {
          acc[row.key] = row.count;
          return acc;
        }, {})
      );
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load plant dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="accounts-page">
      <div className="accounts-header">
        <div>
          <h4 className="mb-1">Plant Manager Dashboard</h4>
          <div className="accounts-subtitle">Plant operations ke saare modules ek jagah.</div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={load}>Refresh</Button>
          <Button variant="success" onClick={() => navigate("/employee/attendance")}>Attendance</Button>
          <Button variant="dark" onClick={() => navigate("/employee/my-idcard")}>ID Card</Button>
        </div>
      </div>

      {err ? <Alert variant="danger">{err}</Alert> : null}

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <Card className="shadow-sm">
            <Card.Body>
              <div className="text-muted small">Today Attendance</div>
              <div className="fw-bold fs-5">{attendanceStatus}</div>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-6">
          <Card className="shadow-sm">
            <Card.Body>
              <div className="text-muted small">ID Card Status</div>
              <div className="fw-bold fs-5">{idStatus}</div>
            </Card.Body>
          </Card>
        </div>
      </div>

      <div className="row g-3">
        {visibleModules.length === 0 ? (
          <div className="col-12">
            <Alert variant="warning" className="mb-0">
              No plant module permissions assigned.
            </Alert>
          </div>
        ) : (
          visibleModules.map((module) => (
            <div className="col-md-4 col-lg-3" key={module.path}>
              <Card className="shadow-sm h-100">
                <Card.Body className="d-flex flex-column">
                  <div className="text-muted small mb-1">Records</div>
                  <div className="fw-bold fs-4 mb-2">{counts[module.path] ?? 0}</div>
                  <div className="fw-semibold mb-3">{module.title}</div>
                  <Button
                    variant="outline-primary"
                    className="mt-auto"
                    onClick={() => navigate(module.path)}
                  >
                    Open
                  </Button>
                </Card.Body>
              </Card>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
